/**
 * REST helpers for the voice subsystem. Wraps `apiFetch` so Authorization
 * + apiBase composition is automatic.
 *
 * Phase 1 endpoints (sealed):
 *   POST /api/v1/voice/session-start
 *   POST /api/v1/voice/sessions/:id/end
 *
 * The Go handler returns lowerCamelCase JSON tags, so the response shapes
 * line up with `SessionStartResponse` / `SessionEndResponse` in `./types`.
 */
import { apiFetch } from '@/lib/http';
import type { SessionEndResponse, SessionStartResponse, VoicesResponse } from './types';

export interface SessionStartArgs {
  projectId: string;
  /**
   * The chat session this call binds to. Every voice turn is persisted +
   * broadcast under this id by the backend. Omitted (not "") when no session
   * is open — protobuf-empty would erase the binding.
   */
  chatSessionId?: string;
  signal?: AbortSignal;
}

export async function startVoiceSession(args: SessionStartArgs): Promise<SessionStartResponse> {
  const body: Record<string, unknown> = { projectId: args.projectId };
  // The BE SessionStartRequest carries `chatSessionId` (lowerCamelCase,
  // omitempty). Only send it when present so an absent binding stays absent.
  if (args.chatSessionId) body.chatSessionId = args.chatSessionId;
  const init: RequestInit = {
    method: 'POST',
    body: JSON.stringify(body),
  };
  if (args.signal) init.signal = args.signal;
  return apiFetch<SessionStartResponse>('/api/v1/voice/session-start', init);
}

export interface SessionEndArgs {
  sessionId: string;
  reason?: string;
  /** AbortSignal — pages call endSession on unmount, so most callers omit. */
  signal?: AbortSignal;
}

/**
 * Pushes the user's current on-screen state to an active voice session so the
 * per-turn LLM endpoint can ground answers in what the user is looking at. The
 * body is the same lowerCamelCase ViewContext shape the chat surface sends via
 * protobuf. Fire-and-forget from the caller's perspective — failures are
 * non-fatal (the assistant just lacks live view context for that turn).
 */
export async function pushVoiceView(
  sessionId: string,
  view: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> {
  const init: RequestInit = {
    method: 'POST',
    body: JSON.stringify(view),
  };
  if (signal) init.signal = signal;
  await apiFetch<{ ok: boolean }>(
    `/api/v1/voice/sessions/${encodeURIComponent(sessionId)}/view`,
    init,
  );
}

/**
 * R10: lists the ElevenLabs workspace My-Voices catalog (filtered server-side
 * to the multilingual_v2 model, sorted by name) for the settings picker. No
 * hardcoded voice ids — the catalog is fetched from EL at runtime. Returns the
 * default-only catalog when the backend has no ElevenLabs workspace key wired.
 */
export async function listVoices(search?: string, signal?: AbortSignal): Promise<VoicesResponse> {
  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  const init: RequestInit = { method: 'GET' };
  if (signal) init.signal = signal;
  return apiFetch<VoicesResponse>(`/api/v1/voice/voices${qs}`, init);
}

export async function endVoiceSession(args: SessionEndArgs): Promise<SessionEndResponse> {
  // Voice turns are persisted into the bound chat session as they happen, so
  // the end-request carries no transcript anymore — it just closes the call.
  const body: Record<string, unknown> = { reason: args.reason ?? 'user_end' };
  const init: RequestInit = {
    method: 'POST',
    body: JSON.stringify(body),
  };
  if (args.signal) init.signal = args.signal;
  return apiFetch<SessionEndResponse>(
    `/api/v1/voice/sessions/${encodeURIComponent(args.sessionId)}/end`,
    init,
  );
}
